import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import AppLayout from "../components/AppLayout";
import DataTable from "../components/ui/DataTable";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  CalendarRange,
  Search,
  X,
  MessageCircle,
  Loader2,
  Pencil,
  Send,
  Mail,
  CheckCircle2,
} from "lucide-react";

const FILTER_STORAGE_KEY = "orders_filter_range";
const formatDateInput = (date) => date.toISOString().slice(0, 10);
const API_URL = process.env.REACT_APP_API_URL;

const fmtEnvioFecha = (v) => {
  if (!v) return "";
  try {
    return new Date(v).toLocaleDateString("es-CO");
  } catch (_) {
    return "";
  }
};

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [empresa, setEmpresa] = useState("");
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const today = useMemo(() => formatDateInput(new Date()), []);

  const [filters, setFilters] = useState(() => {
    try {
      const saved = JSON.parse(
        sessionStorage.getItem(FILTER_STORAGE_KEY) || "{}",
      );
      if (saved.startDate && saved.endDate) return saved;
    } catch (_) {}
    return { startDate: today, endDate: today };
  });

  const [showWAModal, setShowWAModal] = useState(false);
  const [waRow, setWaRow] = useState(null);
  const [waPhone, setWaPhone] = useState("");
  const [waPhoneOriginal, setWaPhoneOriginal] = useState("");
  const [waEditing, setWaEditing] = useState(false);
  const [waSending, setWaSending] = useState(false);

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailRow, setEmailRow] = useState(null);
  const [emailAddress, setEmailAddress] = useState("");
  const [emailAddressOriginal, setEmailAddressOriginal] = useState("");
  const [emailEditing, setEmailEditing] = useState(false);
  const [emailSending, setEmailSending] = useState(false);

  useEffect(() => {
    sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    fetch(`${API_URL}/util/param_sist`)
      .then((r) => r.json())
      .then((d) => {
        if (d?.empresa) setEmpresa(d.empresa);
      })
      .catch(() => {});
  }, []);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/orders`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          startDate: filters.startDate,
          endDate: filters.endDate,
          search,
        },
      });
      setOrders(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error(error);
      toast.error("No se pudieron cargar las órdenes");
    } finally {
      setLoading(false);
    }
  }, [filters.startDate, filters.endDate, search, token]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const clearFilters = () => {
    const def = { startDate: today, endDate: today };
    setFilters(def);
    setSearch("");
    sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(def));
  };

  const openWAModal = async (e, row) => {
    e.stopPropagation();
    setWaRow(row);
    setWaEditing(false);
    let phone = "";
    if (row.nit) {
      try {
        const { data } = await axios.get(
          `${API_URL}/whatsapp/telefono-propietario/${row.nit}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        phone = data.telefono || "";
      } catch (_) {}
    }
    setWaPhone(phone);
    setWaPhoneOriginal(phone);
    setShowWAModal(true);
  };

  const handleSendWAOrden = async () => {
    if (!waPhone.trim()) {
      toast.warning("Ingresa un número de teléfono");
      return;
    }
    setWaSending(true);
    try {
      if (waEditing && waPhone !== waPhoneOriginal && waRow?.nit) {
        await axios.patch(
          `${API_URL}/whatsapp/propietario/${waRow.nit}/telefono`,
          { telefono: waPhone },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setWaPhoneOriginal(waPhone);
        setWaEditing(false);
      }
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      await axios.post(
        `${API_URL}/whatsapp/enviar-resultado-orden`,
        {
          paciente_cod: waRow.paciente_cod,
          empresa,
          telefono: waPhone,
          enviado_por: user?.usuario || user?.id || null,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Resultados enviados por WhatsApp correctamente");
      await fetchOrders();
      setShowWAModal(false);
    } catch (err) {
      toast.error(err.response?.data?.error || "Error enviando por WhatsApp");
    } finally {
      setWaSending(false);
    }
  };

  const openEmailModal = async (e, row) => {
    e.stopPropagation();
    setEmailRow(row);
    setEmailEditing(false);
    let email = "";
    if (row.nit) {
      try {
        const { data } = await axios.get(
          `${API_URL}/email/correo-propietario/${row.nit}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        email = data.email || "";
      } catch (_) {}
    }
    setEmailAddress(email);
    setEmailAddressOriginal(email);
    setShowEmailModal(true);
  };

  const handleSendEmailOrden = async () => {
    if (!emailAddress.trim()) {
      toast.warning("Ingresa un correo electrónico");
      return;
    }
    setEmailSending(true);
    try {
      if (emailEditing && emailAddress !== emailAddressOriginal && emailRow?.nit) {
        await axios.patch(
          `${API_URL}/email/propietario/${emailRow.nit}/correo`,
          { email: emailAddress },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setEmailAddressOriginal(emailAddress);
        setEmailEditing(false);
      }
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      await axios.post(
        `${API_URL}/email/enviar-resultado-orden`,
        {
          paciente_cod: emailRow.paciente_cod,
          correo: emailAddress,
          enviado_por: user?.usuario || user?.id || null,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Resultados enviados por correo correctamente");
      await fetchOrders();
      setShowEmailModal(false);
    } catch (err) {
      toast.error(err.response?.data?.error || "Error enviando por correo");
    } finally {
      setEmailSending(false);
    }
  };

  const columns = [
    {
      header: "Código",
      accessor: "paciente_cod",
      render: (v) => (
        <span className="font-mono text-brand-700 font-semibold text-xs bg-brand-50 px-2 py-0.5 rounded">
          {v}
        </span>
      ),
    },
    { header: "Fecha", accessor: "fecha" },
    { header: "Sede", accessor: "sede_codigo", render: (v) => v || "—" },
    { header: "Convenio", accessor: "propietario", render: (v) => v || "—" },
    {
      header: "Paciente",
      accessor: "paciente",
      render: (v, row) =>
        v ? (
          <div>
            <p className="font-medium text-gray-800">{v}</p>
            {row.paciente_propietario && (
              <p className="text-xs text-gray-400">
                Propietario: {row.paciente_propietario}
              </p>
            )}
          </div>
        ) : (
          "—"
        ),
    },
    { header: "Especie", accessor: "especie", render: (v) => v || "—" },
    { header: "Raza", accessor: "raza", render: (v) => v || "—" },
    {
      header: "Exámenes",
      accessor: "examenes",
      render: (v) =>
        v ? (
          <div className="flex flex-wrap gap-1">
            {v.split(", ").map((ex) => (
              <span
                key={ex}
                className="text-xs bg-brand-50 text-brand-700 border border-brand-200 rounded px-1.5 py-0.5 whitespace-nowrap"
              >
                {ex}
              </span>
            ))}
          </div>
        ) : (
          "—"
        ),
    },
    {
      header: "Cliente",
      accessor: "cliente",
      render: (v, row) => v || row.clte_codigo || "—",
    },
    {
      header: "Registrado por",
      accessor: "registrado_por",
      render: (v) => v || "—",
    },
    {
      header: "Acción",
      accessor: "paciente_cod",
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={(e) => openWAModal(e, row)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-semibold text-xs transition-colors ${
              row.wa_enviado
                ? "bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700"
                : "bg-white border-emerald-200 text-emerald-600 hover:bg-emerald-50"
            }`}
            title={
              row.wa_enviado
                ? `Enviado por WhatsApp${
                    row.wa_enviado_fecha
                      ? ` el ${fmtEnvioFecha(row.wa_enviado_fecha)}`
                      : ""
                  } · clic para reenviar`
                : "Pendiente de envío por WhatsApp"
            }
          >
            {row.wa_enviado ? (
              <CheckCircle2 className="h-3.5 w-3.5" />
            ) : (
              <MessageCircle className="h-3.5 w-3.5" />
            )}
            {row.wa_enviado ? "WhatsApp enviado" : "WhatsApp"}
          </button>
          <button
            type="button"
            onClick={(e) => openEmailModal(e, row)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-semibold text-xs transition-colors ${
              row.email_enviado
                ? "bg-sky-600 border-sky-600 text-white hover:bg-sky-700"
                : "bg-white border-sky-200 text-sky-600 hover:bg-sky-50"
            }`}
            title={
              row.email_enviado
                ? `Enviado por correo${
                    row.email_enviado_fecha
                      ? ` el ${fmtEnvioFecha(row.email_enviado_fecha)}`
                      : ""
                  } · clic para reenviar`
                : "Pendiente de envío por correo"
            }
          >
            {row.email_enviado ? (
              <CheckCircle2 className="h-3.5 w-3.5" />
            ) : (
              <Mail className="h-3.5 w-3.5" />
            )}
            {row.email_enviado ? "Correo enviado" : "Correo"}
          </button>
        </div>
      ),
    },
  ];

  return (
    <AppLayout>
      <div className="p-6 space-y-5">
        {/* Cabecera */}
        <div className="flex items-center gap-3">
          <div className="p-2 bg-brand-100 rounded-xl">
            <Activity className="h-5 w-5 text-brand-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">
              Órdenes del sistema
            </h1>
            <p className="text-sm text-gray-500">
              Consulta y gestiona las órdenes registradas
            </p>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-2xl border border-brand-100 shadow-card p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[140px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Fecha inicio
              </label>
              <input
                type="date"
                className="w-full border border-brand-200 rounded-xl px-3 py-2 text-sm text-gray-700 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition-all bg-brand-50"
                value={filters.startDate}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, startDate: e.target.value }))
                }
              />
            </div>
            <div className="flex-1 min-w-[140px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Fecha fin
              </label>
              <input
                type="date"
                className="w-full border border-brand-200 rounded-xl px-3 py-2 text-sm text-gray-700 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition-all bg-brand-50"
                value={filters.endDate}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, endDate: e.target.value }))
                }
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Buscar
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-brand-400" />
                <input
                  type="text"
                  className="w-full pl-8 pr-4 border border-brand-200 rounded-xl py-2 text-sm text-gray-700 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition-all bg-brand-50"
                  value={search}
                  placeholder="Cliente, propietario o examen"
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <button
              type="button"
              onClick={clearFilters}
              className="flex items-center gap-1.5 px-4 py-2 border border-brand-200 rounded-xl text-sm text-brand-600 bg-white hover:bg-brand-50 transition-all"
            >
              <X className="h-3.5 w-3.5" />
              Limpiar
            </button>
          </div>

          <div className="mt-3 flex items-center gap-2">
            <CalendarRange className="h-3.5 w-3.5 text-brand-400" />
            <span className="text-xs text-brand-600 bg-brand-50 border border-brand-200 rounded-full px-3 py-0.5">
              {filters.startDate} → {filters.endDate}
            </span>
            <span className="text-xs text-gray-400">
              {orders.length} orden(es)
            </span>
          </div>
        </div>

        {/* Tabla */}
        <DataTable
          columns={columns}
          data={orders}
          loading={loading}
          searchable={false}
          persistKey="orders_table_state"
          onRowClick={(row) =>
            navigate(
              `/order-exams?paciente_cod=${row.paciente_cod}${row.nit ? `&nit=${encodeURIComponent(row.nit)}` : ""}`,
            )
          }
          emptyMessage="No hay órdenes en ese rango de fechas."
        />
      </div>

      {/* Modal envío por WhatsApp */}
      {showWAModal && waRow && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-emerald-100 rounded-xl">
                  <MessageCircle className="h-5 w-5 text-emerald-600" />
                </div>
                <h3 className="text-base font-semibold text-gray-800">
                  Enviar resultados por WhatsApp
                </h3>
              </div>
              <button
                onClick={() => setShowWAModal(false)}
                className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="bg-gray-50 rounded-xl p-3 text-sm text-gray-700 space-y-1">
                <p>
                  <span className="font-medium">Orden:</span>{" "}
                  {waRow.paciente_cod}
                </p>
                <p>
                  <span className="font-medium">Fecha:</span> {waRow.fecha}
                </p>
                {waRow.paciente && (
                  <p>
                    <span className="font-medium">Paciente:</span>{" "}
                    {waRow.paciente}
                  </p>
                )}
                {waRow.propietario && (
                  <p>
                    <span className="font-medium">Propietario:</span>{" "}
                    {waRow.propietario}
                  </p>
                )}
                <p className="text-xs text-amber-600">
                  Se enviarán todos los resultados validados de esta orden en un
                  solo PDF.
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Número de WhatsApp del propietario
                </label>
                <div className="flex gap-2">
                  <input
                    type="tel"
                    value={waPhone}
                    onChange={(e) => setWaPhone(e.target.value)}
                    disabled={!waEditing}
                    placeholder="Ej: 3001234567"
                    className="flex-1 border border-brand-200 rounded-xl px-3 py-2 text-sm bg-brand-50 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 disabled:bg-gray-100 disabled:text-gray-500"
                  />
                  <button
                    onClick={() => setWaEditing((v) => !v)}
                    className={`p-2 rounded-xl border transition-all ${waEditing ? "bg-brand-100 border-brand-300 text-brand-700" : "border-gray-200 text-gray-500 hover:border-brand-200"}`}
                    title={waEditing ? "Cancelar edición" : "Editar número"}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                </div>
                {waEditing && waPhone !== waPhoneOriginal && (
                  <p className="text-xs text-amber-600 mt-1">
                    El número actualizado se guardará en la base de datos al
                    enviar.
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-2 justify-end p-5 border-t border-gray-100">
              <button
                onClick={() => setShowWAModal(false)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={handleSendWAOrden}
                disabled={waSending || !waPhone.trim()}
                className="inline-flex items-center gap-2 px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {waSending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {waSending ? "Enviando…" : "Enviar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEmailModal && emailRow && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-sky-100 rounded-xl">
                  <Mail className="h-5 w-5 text-sky-600" />
                </div>
                <h3 className="text-base font-semibold text-gray-800">
                  Enviar resultados por correo
                </h3>
              </div>
              <button
                onClick={() => setShowEmailModal(false)}
                className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Correo del propietario
                </label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={emailAddress}
                    onChange={(e) => setEmailAddress(e.target.value)}
                    disabled={!emailEditing}
                    placeholder="ejemplo@correo.com"
                    className="flex-1 border border-brand-200 rounded-xl px-3 py-2 text-sm bg-brand-50 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 disabled:bg-gray-100 disabled:text-gray-500"
                  />
                  <button
                    onClick={() => setEmailEditing((v) => !v)}
                    className={`p-2 rounded-xl border transition-all ${emailEditing ? "bg-brand-100 border-brand-300 text-brand-700" : "border-gray-200 text-gray-500 hover:border-brand-200"}`}
                    title={emailEditing ? "Cancelar edición" : "Editar correo"}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                </div>
                {emailEditing && emailAddress !== emailAddressOriginal && (
                  <p className="text-xs text-amber-600 mt-1">
                    El correo actualizado se guardará en la base de datos al enviar.
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-2 justify-end p-5 border-t border-gray-100">
              <button
                onClick={() => setShowEmailModal(false)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={handleSendEmailOrden}
                disabled={emailSending || !emailAddress.trim()}
                className="inline-flex items-center gap-2 px-5 py-2 bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {emailSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {emailSending ? "Enviando…" : "Enviar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default OrdersPage;
